import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { getArticleSchema, getBreadcrumbSchema, getFAQSchema } from '@/lib/schema';

export default function ItaobuySpreadsheetPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd schema={getArticleSchema(
        '/itaobuy-spreadsheet',
        'Itaobuy Spreadsheet: La Guida Italiana Definitiva per lo Shopping Internazionale nel 2026',
        'Guida completa su Itaobuy Spreadsheet. Scopri come funziona, categorie prodotti, prezzi, sicurezza e vantaggi per lo shopping internazionale. Confronto dettagliato e FAQ per acquirenti italiani.',
        '2026-01-15',
        '2026-07-14',
        'Esperto Itaobuy Spreadsheet',
        undefined,
        'PT9M'
      )} />
      <JsonLd schema={getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Itaobuy Spreadsheet', url: '/itaobuy-spreadsheet' },
      ])} />
      <JsonLd schema={getFAQSchema([
        { q: 'Itaobuy Spreadsheet è gratuito?', a: "Sì, l'accesso allo spreadsheet Itaobuy è completamente gratuito. Puoi navigare tutte le categorie e visualizzare i prodotti senza alcun costo." },
        { q: 'I prodotti su Itaobuy Spreadsheet sono originali?', a: 'Lo spreadsheet include sia prodotti originali che replica di alta qualità. Ogni prodotto è chiaramente etichettato con la sua categoria di autenticità.' },
        { q: 'Come vengono aggiornati i prezzi?', a: 'I prezzi vengono aggiornati regolarmente dal team di Itaobuy Spreadsheet per riflettere le fluttuazioni del mercato.' },
        { q: 'Posso richiedere prodotti specifici?', a: "Sì, la community di Itaobuy Spreadsheet è molto attiva e accoglie richieste di nuovi prodotti attraverso i canali ufficiali." },
        { q: 'È sicuro acquistare usando Itaobuy Spreadsheet?', a: 'Assolutamente sì. Lo spreadsheet ti indirizza verso venditori verificati con una comprovata esperienza positiva.' },
      ])} />
      <Navbar />

      {/* Article Hero */}
      <section className="w-full px-6 md:px-10 pt-28 pb-12 bg-background-100">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs font-body text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-400 whitespace-nowrap">Itaobuy Spreadsheet</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-label text-xs font-semibold uppercase tracking-wider whitespace-nowrap mb-4">
            Guida Completa
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
            Itaobuy Spreadsheet: La Guida Italiana Definitiva per lo Shopping Internazionale nel 2026
          </h1>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-6 pt-6 border-t border-background-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center">
                <span className="font-label text-xs font-semibold text-primary-700">ES</span>
              </div>
              <div>
                <p className="font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">{t('article.author_name')}</p>
                <p className="font-body text-[10px] text-foreground-500 whitespace-nowrap">{t('article.author')}</p>
              </div>
            </div>
            <span className="text-foreground-300 hidden md:block">|</span>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-foreground-400 text-sm"></i>
              </span>
              <span className="font-body text-xs text-foreground-500 whitespace-nowrap">{t('article.updated')}: Luglio 2026</span>
            </div>
            <span className="text-foreground-300 hidden md:block">|</span>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-foreground-400 text-sm"></i>
              </span>
              <span className="font-body text-xs text-foreground-500 whitespace-nowrap">{t('article.read_time')}: 9 {t('article.minutes')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Main content */}
            <div className="flex-1 max-w-none">
              {/* Definition Box - Featured Snippet */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6 mb-10">
                <p className="font-label text-xs font-semibold text-accent-700 uppercase tracking-wider whitespace-nowrap mb-3">Cos&apos;è Itaobuy Spreadsheet?</p>
                <p className="font-body text-sm md:text-base text-foreground-800 leading-relaxed">
                  <strong>Itaobuy Spreadsheet</strong> è una raccolta organizzata di prodotti, informazioni e link verificati che permette agli acquirenti italiani di navigare i marketplace internazionali in modo più rapido, sicuro ed efficiente. Funziona come un database centralizzato che aggrega prodotti di qualità, prezzi aggiornati e valutazioni dei venditori, eliminando la necessità di cercare manualmente tra migliaia di inserzioni.
                </p>
              </div>

              {/* Intro */}
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                Se sei un appassionato di shopping online e hai sempre desiderato accedere ai migliori prodotti dai marketplace internazionali senza perdere ore nella ricerca, <strong>Itaobuy Spreadsheet</strong> è la soluzione che stavi cercando. In questa guida completa, analizzeremo ogni aspetto di questo strumento essenziale: come funziona, quali categorie copre, come usarlo in sicurezza e perché sta diventando il punto di riferimento per migliaia di acquirenti italiani.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Come Funziona Itaobuy Spreadsheet</h2>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Il funzionamento di <strong>Itaobuy Spreadsheet</strong> è sorprendentemente semplice ma estremamente efficace. Lo spreadsheet funge da intermediario tra te e i migliori marketplace, organizzando migliaia di prodotti in categorie chiare e facilmente navigabili. Ogni prodotto include informazioni essenziali come il nome del venditore, il prezzo in yuan, le valutazioni di qualità e i link diretti per l&apos;acquisto.
              </p>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A differenza della navigazione tradizionale sui marketplace, dove devi sfogliare decine di pagine per trovare un prodotto di qualità, lo <strong>spreadsheet Itaobuy</strong> ti presenta direttamente una selezione curata. I prodotti sono stati verificati da esperti che conoscono il mercato e sanno distinguere un venditore affidabile da uno potenzialmente problematico.
              </p>

              {/* CTA inside article */}
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 md:p-6 my-8 text-center">
                <p className="font-label text-sm font-semibold text-primary-800 mb-3 whitespace-nowrap">
                  Vuoi accedere subito allo spreadsheet completo?
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label text-xs font-semibold uppercase tracking-wider rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Accedi allo Spreadsheet
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Categorie di Prodotti Disponibili</h2>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                Una delle caratteristiche più apprezzate di <strong>Itaobuy Spreadsheet</strong> è l&apos;ampia copertura di categorie. Che tu stia cercando scarpe, abbigliamento, accessori o articoli particolari, troverai una sezione dedicata con prodotti attentamente selezionati.
              </p>

              {/* Categories Table */}
              <div className="overflow-x-auto mb-8 rounded-xl border border-background-200">
                <table className="w-full text-left font-body text-sm">
                  <thead className="bg-background-100">
                    <tr>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Categoria</th>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Tipo Prodotti</th>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Qualità Media</th>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Prezzo Medio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {[
                      { cat: "Shoes", type: "Sneakers, casual, sportive", quality: "9.0", price: "¥350-600" },
                      { cat: "Hoodies/Sweaters", type: "Felpe, maglioni, zip-up", quality: "8.8", price: "¥120-250" },
                      { cat: "T-Shirts", type: "Basic, grafiche, limited", quality: "8.5", price: "¥60-150" },
                      { cat: "Jackets", type: "Bomber, giubbotti, piumini", quality: "8.7", price: "¥200-500" },
                      { cat: "Pants/Shorts", type: "Jeans, cargo, shorts", quality: "8.6", price: "¥100-300" },
                      { cat: "Accessories", type: "Occhiali, gioielli, portafogli", quality: "8.4", price: "¥30-150" },
                      { cat: "Bags", type: "Zaini, borse, borselli", quality: "8.9", price: "¥150-400" },
                      { cat: "Jersey", type: "Maglie calcio, basket, sport", quality: "8.3", price: "¥80-180" },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-background-50 transition-colors">
                        <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">{row.cat}</td>
                        <td className="px-4 py-3 text-foreground-600 text-xs">{row.type}</td>
                        <td className="px-4 py-3 text-accent-600 font-semibold text-xs whitespace-nowrap">★ {row.quality}</td>
                        <td className="px-4 py-3 text-foreground-600 text-xs whitespace-nowrap">{row.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Vantaggi di Usare Itaobuy Spreadsheet</h2>
              <div className="space-y-4 mb-8">
                {[
                  { title: "Risparmio di Tempo", desc: "Invece di passare ore a cercare prodotti, lo spreadsheet ti offre una selezione già filtrata e verificata. In pochi click puoi trovare esattamente ciò che cerchi e procedere all'acquisto." },
                  { title: "Qualità Garantita", desc: "Ogni prodotto nel database Itaobuy Spreadsheet è stato controllato da esperti. Le valutazioni di qualità ti aiutano a capire subito se un articolo vale l'acquisto o se è meglio cercare un'alternativa." },
                  { title: "Prezzi Competitivi", desc: "Grazie all'accesso diretto ai marketplace, i prezzi sono notevolmente inferiori rispetto ai rivenditori tradizionali. Puoi risparmiare fino al 40-60% su prodotti di qualità comparabile." },
                  { title: "Aggiornamenti Costanti", desc: "Il database viene aggiornato regolarmente per riflettere le ultime tendenze, i nuovi prodotti e i cambiamenti di prezzo. Non rimani mai indietro rispetto al mercato." },
                  { title: "Community Italiana", desc: "Entra a far parte di una community di acquirenti italiani che condividono recensioni, consigli e feedback sui prodotti. Non sei mai solo nel tuo percorso di shopping." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200">
                    <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-600 text-sm"></i>
                    </span>
                    <div>
                      <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="font-body text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Come Iniziare con Itaobuy Spreadsheet: Guida Passo-Passo</h2>
              <div className="space-y-6 mb-8">
                {[
                  { step: "01", title: "Accedi allo Spreadsheet", desc: "Visita il sito principale e accedi allo spreadsheet Itaobuy. La navigazione è intuitiva: troverai tutte le categorie organizzate in modo chiaro e accessibile." },
                  { step: "02", title: "Scegli la Categoria", desc: "Seleziona la categoria che ti interessa dal menu a discesa o dalla griglia di navigazione. Puoi esplorare scarpe, abbigliamento, accessori e molto altro con un semplice click." },
                  { step: "03", title: "Esplora i Prodotti", desc: "Sfoglia la selezione curata di prodotti all'interno della categoria scelta. Ogni prodotto include nome, prezzo, valutazione di qualità e link diretto per l'acquisto." },
                  { step: "04", title: "Verifica la Qualità", desc: "Controlla sempre la valutazione di qualità e le recensioni prima di procedere. I prodotti con valutazione superiore a 8.5 sono generalmente considerati ottimi acquisti." },
                  { step: "05", title: "Procedi all'Acquisto", desc: "Una volta trovato il prodotto che fa per te, utilizza il link fornito per andare direttamente alla pagina di acquisto e completare l'ordine in pochi minuti." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary-500 text-background-50 font-heading text-lg md:text-xl font-bold flex items-center justify-center flex-shrink-0">
                      {item.step}
                    </span>
                    <div>
                      <h3 className="font-label text-sm md:text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="font-body text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA 2 */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6 my-8 text-center">
                <p className="font-heading text-lg md:text-xl font-semibold text-accent-800 mb-2">
                  Pronto a iniziare?
                </p>
                <p className="font-body text-sm text-accent-700 mb-4">
                  Scopri subito il database completo e inizia a risparmiare sui tuoi acquisti.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 font-label text-xs font-semibold uppercase tracking-wider rounded-full hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Vai allo Spreadsheet Ora
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Sicurezza e Affidabilità</h2>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                La sicurezza è una priorità assoluta quando si fa shopping su marketplace internazionali. <strong>Itaobuy Spreadsheet</strong> include solo prodotti da venditori verificati con track record positivo. Tuttavia, è importante seguire alcune precauzioni di base per garantire un&apos;esperienza di acquisto sicura e senza problemi.
              </p>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Consigliamo sempre di utilizzare metodi di pagamento sicuri, di leggere attentamente le descrizioni dei prodotti e di contattare il venditore in caso di dubbi prima di finalizzare l&apos;acquisto. Per maggiori informazioni sulla sicurezza, visita la nostra <Link to="/sicurezza" className="text-primary-600 hover:text-primary-500 underline cursor-pointer">pagina dedicata alla sicurezza</Link>.
              </p>

              {/* Comparison Table */}
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-4">Confronto: Con e Senza Itaobuy Spreadsheet</h2>
              <div className="overflow-x-auto mb-8 rounded-xl border border-background-200">
                <table className="w-full text-left font-body text-sm">
                  <thead className="bg-background-100">
                    <tr>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Aspetto</th>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Senza Itaobuy Spreadsheet</th>
                      <th className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 uppercase tracking-wider whitespace-nowrap">Con Itaobuy Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    <tr>
                      <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">Tempo di Ricerca</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs">1-3 ore per prodotto</td>
                      <td className="px-4 py-3 text-accent-700 font-semibold text-xs whitespace-nowrap">5-10 minuti per prodotto</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">Qualità Verificata</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs">Devi verificare manualmente</td>
                      <td className="px-4 py-3 text-accent-700 font-semibold text-xs whitespace-nowrap">Già verificata dagli esperti</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">Prezzi Competitivi</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs">Difficile confrontare</td>
                      <td className="px-4 py-3 text-accent-700 font-semibold text-xs whitespace-nowrap">Facile confronto diretto</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">Aggiornamenti</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs">Devi monitorare tu</td>
                      <td className="px-4 py-3 text-accent-700 font-semibold text-xs whitespace-nowrap">Aggiornamenti automatici</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">Supporto Community</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs">Nessuno</td>
                      <td className="px-4 py-3 text-accent-700 font-semibold text-xs whitespace-nowrap">Community italiana attiva</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* FAQ */}
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-12 mb-6">Domande Frequenti su Itaobuy Spreadsheet</h2>
              <div className="space-y-4 mb-8">
                {[
                  { q: "Itaobuy Spreadsheet è gratuito?", a: "Sì, l'accesso allo spreadsheet Itaobuy è completamente gratuito. Puoi navigare tutte le categorie e visualizzare i prodotti senza alcun costo. I prezzi che vedi sono quelli effettivi dei prodotti sui marketplace." },
                  { q: "I prodotti su Itaobuy Spreadsheet sono originali?", a: "Lo spreadsheet include sia prodotti originali che replica di alta qualità. Ogni prodotto è chiaramente etichettato con la sua categoria di autenticità, permettendoti di fare una scelta informata in base alle tue preferenze e al tuo budget." },
                  { q: "Come vengono aggiornati i prezzi?", a: "I prezzi vengono aggiornati regolarmente dal team di Itaobuy Spreadsheet per riflettere le fluttuazioni del mercato. Tuttavia, ti consigliamo di verificare sempre il prezzo attuale sulla pagina del prodotto prima di procedere all'acquisto." },
                  { q: "Posso richiedere prodotti specifici?", a: "Sì, la community di Itaobuy Spreadsheet è molto attiva e accoglie richieste di nuovi prodotti. Puoi suggerire categorie o articoli specifici che vorresti vedere inclusi nel database attraverso i canali ufficiali." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-background-200 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-label text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors">
                      {faq.q}
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line text-foreground-400"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="font-body text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
                <details className="group border border-background-200 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-label text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors">
                    È sicuro acquistare usando Itaobuy Spreadsheet?
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="font-body text-xs md:text-sm text-foreground-600 leading-relaxed">
                      Assolutamente sì. Lo spreadsheet ti indirizza verso venditori verificati con una comprovata esperienza positiva. Per una guida completa sulla sicurezza, visita la nostra <Link to="/sicurezza" className="text-primary-600 hover:text-primary-500 underline cursor-pointer">pagina dedicata alla sicurezza</Link> dove troverai tutti i consigli per acquistare in tranquillità.
                    </p>
                  </div>
                </details>
              </div>

              {/* Final CTA */}
              <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center mt-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                  Inizia Oggi con Itaobuy Spreadsheet
                </h2>
                <p className="font-body text-sm text-background-50/80 mb-6 max-w-md mx-auto leading-relaxed">
                  Unisciti a migliaia di acquirenti italiani che stanno già risparmiando tempo e denaro con lo spreadsheet più completo per lo shopping internazionale.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 font-label text-sm font-semibold uppercase tracking-wider rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('article.cta')}
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-8 py-3.5 border border-background-50/40 text-background-50 font-label text-sm font-semibold uppercase tracking-wider rounded-full hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('article.back_home')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                  <h4 className="font-label text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 whitespace-nowrap">{t('article.author')}</h4>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-primary-200 flex items-center justify-center">
                      <span className="font-label text-sm font-bold text-primary-700">ES</span>
                    </div>
                    <div>
                      <p className="font-label text-sm font-semibold text-foreground-800 whitespace-nowrap">{t('article.author_name')}</p>
                      <p className="font-body text-[10px] text-foreground-500">Analista Mercato</p>
                    </div>
                  </div>
                  <p className="font-body text-xs text-foreground-600 leading-relaxed">
                    {t('article.author_bio')}
                  </p>
                </div>

                {/* Quick Links */}
                <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                  <h4 className="font-label text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4 whitespace-nowrap">Link Rapidi</h4>
                  <div className="space-y-2.5">
                    <Link to="/" className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-home-line text-xs"></i>
                      </span>
                      Torna alla Home
                    </Link>
                    <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-footprint-line text-xs"></i>
                      </span>
                      Categoria Shoes
                    </a>
                    <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-t-shirt-line text-xs"></i>
                      </span>
                      Hoodies/Sweaters
                    </a>
                    <Link to="/sicurezza" className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-shield-check-line text-xs"></i>
                      </span>
                      Centro Sicurezza
                    </Link>
                    <Link to="/database" className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-database-2-line text-xs"></i>
                      </span>
                      Database Prodotti
                    </Link>
                  </div>
                </div>

                {/* CTA Sidebar Card */}
                <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 text-center">
                  <span className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shopping-bag-3-line text-accent-600 text-lg"></i>
                  </span>
                  <p className="font-label text-xs font-semibold text-accent-800 mb-3 whitespace-nowrap">
                    Pronto per lo Shopping?
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 font-label text-xs font-semibold rounded-full hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Vai allo Spreadsheet
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}